// src/pages/crm/PageCrmSamples.jsx
import { useState, useEffect, useCallback } from "react";
import * as api from "../../apiService";
import { useToast } from "../../components/Toast";

// ── Status config ─────────────────────────────────────────────────────────────

export const TRIAL_STATUSES = [
  { key: "SENT",     label: "Sent",     color: "#0891b2", bg: "#ecfeff", border: "#a5f3fc" },
  { key: "IN_TRIAL", label: "In Trial", color: "#7c3aed", bg: "#f5f3ff", border: "#ddd6fe" },
  { key: "PASSED",   label: "Passed",   color: "#15803d", bg: "#f0fdf4", border: "#bbf7d0" },
  { key: "FAILED",   label: "Failed",   color: "#b91c1c", bg: "#fef2f2", border: "#fca5a5" },
];
export const STATUS_MAP = Object.fromEntries(TRIAL_STATUSES.map(s => [s.key, s]));

const COMPANY_BTN = {
  AMI: { color: "#1d4ed8", bg: "#eff6ff", border: "#bfdbfe" },
  AMT: { color: "#0d9488", bg: "#f0fdfa", border: "#99f6e4" },
};

const ACTIVE_OPP_STAGES = ["SHORTLISTED", "LEAD", "TRIAL_DONE", "PROPOSAL_SENT", "NEGOTIATION"];

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-AE", { day: "2-digit", month: "short", year: "numeric" });
}

function StatusBadge({ status }) {
  const cfg = STATUS_MAP[status] ?? STATUS_MAP["SENT"];
  return (
    <span style={{
      fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 10,
      background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
      whiteSpace: "nowrap",
    }}>{cfg.label}</span>
  );
}

function CompanyBadge({ company }) {
  const cfg = COMPANY_BTN[company] ?? {};
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: "1px 5px", borderRadius: 3,
      background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
    }}>{company}</span>
  );
}

// ── Opportunity typeahead for create modal ────────────────────────────────────

function OppPicker({ opps, value, onChange }) {
  const selected = opps.find(o => String(o.id) === String(value));
  const [query, setQuery] = useState(selected ? `${selected.accountName} — ${selected.title}` : "");
  const [open,  setOpen]  = useState(false);

  const matches = query.trim().length === 0
    ? opps
    : opps.filter(o =>
        o.title.toLowerCase().includes(query.toLowerCase()) ||
        o.accountName.toLowerCase().includes(query.toLowerCase())
      );

  function pick(o) {
    onChange(String(o.id));
    setQuery(`${o.accountName} — ${o.title}`);
    setOpen(false);
  }

  return (
    <div style={{ position: "relative" }}>
      <input value={query}
        onChange={e => { setQuery(e.target.value); onChange(""); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => { setOpen(false); if (!value) setQuery(""); }, 150)}
        placeholder="Search opportunity…"
        autoComplete="off"
        style={{ padding: "6px 10px", border: "1px solid #d1d5db", borderRadius: 6, fontSize: 13, width: "100%", boxSizing: "border-box" }}
      />
      {open && matches.length > 0 && (
        <div style={{
          position: "absolute", top: "100%", left: 0, right: 0, zIndex: 30,
          background: "#fff", border: "1px solid #d1d5db", borderRadius: 6,
          boxShadow: "0 4px 12px rgba(0,0,0,0.12)", maxHeight: 200, overflowY: "auto", marginTop: 2,
        }}>
          {matches.map(o => (
            <div key={o.id} onMouseDown={() => pick(o)}
              style={{ padding: "8px 12px", fontSize: 13, cursor: "pointer", color: "#111827" }}
              onMouseEnter={e => e.currentTarget.style.background = "#f3f4f6"}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}
            >
              <span style={{ fontWeight: 600 }}>{o.accountName}</span>
              <span style={{ color: "#9ca3af", margin: "0 6px" }}>—</span>
              <span>{o.title}</span>
              <span style={{ marginLeft: 8, fontSize: 10, color: o.company === "AMI" ? "#1d4ed8" : "#0d9488", fontWeight: 700 }}>
                {o.company}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Create / Edit modal ───────────────────────────────────────────────────────

function SampleModal({ trial, opps, onSave, onClose }) {
  const isEdit = !!trial?.id;
  const [form, setForm] = useState({
    opportunityId: trial ? String(trial.opportunityId) : "",
    productName:   trial?.productName   ?? "",
    quantity:      trial?.quantity      ?? "",
    application:   trial?.application   ?? "",
    sentDate:      trial?.sentDate      ?? "",
    status:        trial?.status        ?? "SENT",
    trialNotes:    trial?.trialNotes    ?? "",
    failureReason: trial?.failureReason ?? "",
  });
  const [saving, setSaving] = useState(false);
  const [err,    setErr]    = useState(null);

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!isEdit && !form.opportunityId) { setErr("Select an opportunity"); return; }
    if (!form.productName.trim())        { setErr("Product name is required"); return; }
    setSaving(true); setErr(null);
    try {
      const body = {
        productName:   form.productName.trim(),
        quantity:      form.quantity   || null,
        application:   form.application || null,
        sentDate:      form.sentDate   || null,
        status:        form.status,
        trialNotes:    form.trialNotes || null,
        failureReason: form.status === "FAILED" ? (form.failureReason || null) : null,
      };
      if (isEdit) await api.updateTrial(trial.id, body);
      else        await api.createTrial(Number(form.opportunityId), body);
      onSave();
    } catch (ex) {
      setErr(ex.message ?? "Save failed");
    } finally {
      setSaving(false);
    }
  }

  const inp = { padding: "6px 10px", border: "1px solid #d1d5db", borderRadius: 6, fontSize: 13, width: "100%", boxSizing: "border-box" };
  const lbl = { fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4, color: "#374151" };

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
      display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: "#fff", borderRadius: 10, padding: 24, width: 480, maxWidth: "95vw", maxHeight: "92vh", overflowY: "auto" }}>
        <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 16 }}>
          {isEdit ? "Edit Sample Trial" : "Log Sample"}
        </div>
        {err && <div style={{ color: "#b91c1c", fontSize: 12, marginBottom: 10 }}>{err}</div>}
        <form onSubmit={handleSubmit}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>

            {!isEdit && (
              <div>
                <label style={lbl}>Opportunity *</label>
                <OppPicker opps={opps} value={form.opportunityId} onChange={v => set("opportunityId", v)} />
              </div>
            )}
            {isEdit && (
              <div style={{ background: "#f9fafb", borderRadius: 6, padding: "8px 12px", fontSize: 12, color: "#6b7280" }}>
                <span style={{ fontWeight: 600, color: "#374151" }}>{trial.accountName}</span>
                {" — "}{trial.opportunityTitle}
              </div>
            )}

            <div>
              <label style={lbl}>Product Name *</label>
              <input value={form.productName} onChange={e => set("productName", e.target.value)}
                style={inp} placeholder="e.g. AM-1001 Water-based adhesive 55% solids" />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div>
                <label style={lbl}>Quantity</label>
                <input value={form.quantity} onChange={e => set("quantity", e.target.value)}
                  style={inp} placeholder="e.g. 2 kg, 5 litres" />
              </div>
              <div>
                <label style={lbl}>Sent Date</label>
                <input type="date" value={form.sentDate} onChange={e => set("sentDate", e.target.value)} style={inp} />
              </div>
            </div>

            <div>
              <label style={lbl}>Application / Use Case</label>
              <input value={form.application} onChange={e => set("application", e.target.value)}
                style={inp} placeholder="e.g. Laminating PP to foil" />
            </div>

            <div>
              <label style={lbl}>Status</label>
              <select value={form.status} onChange={e => set("status", e.target.value)} style={inp}>
                {TRIAL_STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </div>

            <div>
              <label style={lbl}>Trial Notes</label>
              <textarea value={form.trialNotes} onChange={e => set("trialNotes", e.target.value)}
                rows={3} style={{ ...inp, resize: "vertical" }} placeholder="Customer feedback, observations…" />
            </div>

            {form.status === "FAILED" && (
              <div>
                <label style={lbl}>Failure Reason</label>
                <textarea value={form.failureReason} onChange={e => set("failureReason", e.target.value)}
                  rows={2} style={{ ...inp, resize: "vertical" }} placeholder="Why did the trial fail?" />
              </div>
            )}
          </div>

          <div style={{ display: "flex", gap: 8, marginTop: 20, justifyContent: "flex-end" }}>
            <button type="button" className="btn" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? "Saving…" : isEdit ? "Save Changes" : "Log Sample"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function PageCrmSamples({ myPerms, userEmail, onBack }) {
  const showToast = useToast();
  const [trials,     setTrials]     = useState([]);
  const [opps,       setOpps]       = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [err,        setErr]        = useState(null);
  const [modal,      setModal]      = useState(null); // null | {type:"create"} | {type:"edit", trial}
  const [filterStatus,  setFilterStatus]  = useState("");
  const [filterCompany, setFilterCompany] = useState("");

  const canManage = myPerms.includes("CRM_MANAGE");

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const [t, o] = await Promise.all([api.fetchAllTrials(), api.fetchOpportunities()]);
      setTrials(t);
      setOpps(o.filter(o => ACTIVE_OPP_STAGES.includes(o.stage)));
    } catch (ex) {
      setErr(ex.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleStatusChange(trial, newStatus) {
    await api.updateTrial(trial.id, {
      productName:   trial.productName,
      quantity:      trial.quantity,
      application:   trial.application,
      sentDate:      trial.sentDate,
      status:        newStatus,
      trialNotes:    trial.trialNotes,
      failureReason: trial.failureReason,
    });
    showToast("Status updated");
    load();
  }

  async function handleDelete(trial) {
    if (!window.confirm(`Delete sample "${trial.productName}"?`)) return;
    await api.deleteTrial(trial.id);
    showToast("Sample deleted", "info");
    load();
  }

  const userTrials = canManage ? trials : trials.filter(t => t.assignedTo === userEmail);

  const filtered = userTrials.filter(t =>
    (!filterStatus  || t.status  === filterStatus) &&
    (!filterCompany || t.company === filterCompany)
  );

  // Summary counts
  const counts = Object.fromEntries(TRIAL_STATUSES.map(s => [s.key, 0]));
  userTrials.forEach(t => { if (counts[t.status] !== undefined) counts[t.status]++; });

  const inp = { padding: "5px 10px", border: "1px solid #d1d5db", borderRadius: 6, fontSize: 13 };

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div className="page-header-left">
          <div className="page-title">Sample & Trial Tracking</div>
          <div className="page-subtitle">
            {TRIAL_STATUSES.map((s, i) => (
              <span key={s.key}>
                {i > 0 && <span style={{ color: "#d1d5db" }}> · </span>}
                <span style={{ color: s.color, fontWeight: 600 }}>{counts[s.key]}</span>
                <span style={{ color: "#9ca3af" }}> {s.label}</span>
              </span>
            ))}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={inp}>
            <option value="">All Statuses</option>
            {TRIAL_STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
          <select value={filterCompany} onChange={e => setFilterCompany(e.target.value)} style={inp}>
            <option value="">AMI + AMT</option>
            <option value="AMI">AMI</option>
            <option value="AMT">AMT</option>
          </select>
          <button className="btn btn-primary" onClick={() => setModal({ type: "create" })}>
            + Log Sample
          </button>
        </div>
      </div>

      {err && <div className="alert-warn" style={{ marginBottom: 16 }}>{err}</div>}
      {loading && <div style={{ color: "#9ca3af", fontSize: 13 }}>Loading…</div>}

      {!loading && filtered.length === 0 && (
        <div style={{ color: "#9ca3af", fontSize: 13, marginTop: 24, textAlign: "center" }}>
          No samples found.
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "#f9fafb", borderBottom: "2px solid #e5e7eb" }}>
                {["Product", "Application", "Qty", "Sent", "Customer", "Opportunity", "", "Status", "Assigned To", ""].map((h, i) => (
                  <th key={i} style={{ padding: "8px 12px", textAlign: "left", fontWeight: 600, fontSize: 11, color: "#6b7280", whiteSpace: "nowrap" }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(t => (
                <tr key={t.id} style={{ borderBottom: "1px solid #f3f4f6" }}
                  onMouseEnter={e => e.currentTarget.style.background = "#fafafa"}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                >
                  <td style={{ padding: "10px 12px", fontWeight: 600, color: "#111827", maxWidth: 200 }}>
                    {t.productName}
                  </td>
                  <td style={{ padding: "10px 12px", color: "#6b7280", maxWidth: 180 }}>
                    {t.application ?? "—"}
                  </td>
                  <td style={{ padding: "10px 12px", color: "#374151", whiteSpace: "nowrap" }}>
                    {t.quantity ?? "—"}
                  </td>
                  <td style={{ padding: "10px 12px", color: "#374151", whiteSpace: "nowrap" }}>
                    {fmtDate(t.sentDate)}
                  </td>
                  <td style={{ padding: "10px 12px", fontWeight: 600, color: "#374151" }}>
                    {t.accountName}
                  </td>
                  <td style={{ padding: "10px 12px", color: "#6b7280", maxWidth: 200 }}>
                    {t.opportunityTitle}
                  </td>
                  <td style={{ padding: "10px 12px" }}>
                    <CompanyBadge company={t.company} />
                  </td>
                  <td style={{ padding: "10px 12px" }}>
                    <select value={t.status}
                      onChange={e => handleStatusChange(t, e.target.value)}
                      style={{
                        padding: "3px 6px", borderRadius: 4, fontSize: 11, fontWeight: 700, cursor: "pointer",
                        border: `1px solid ${STATUS_MAP[t.status]?.border ?? "#d1d5db"}`,
                        background: STATUS_MAP[t.status]?.bg ?? "#f9fafb",
                        color: STATUS_MAP[t.status]?.color ?? "#374151",
                      }}>
                      {TRIAL_STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                    </select>
                  </td>
                  <td style={{ padding: "10px 12px", color: "#9ca3af", fontSize: 12 }}>
                    {t.assignedTo}
                  </td>
                  <td style={{ padding: "10px 12px", whiteSpace: "nowrap" }}>
                    <button onClick={() => setModal({ type: "edit", trial: t })}
                      style={{ background: "none", border: "none", cursor: "pointer", color: "#6366f1", fontSize: 12, fontWeight: 600, marginRight: 10 }}>
                      Edit
                    </button>
                    {canManage && (
                      <button onClick={() => handleDelete(t)}
                        style={{ background: "none", border: "none", cursor: "pointer", color: "#b91c1c", fontSize: 12 }}>
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(modal?.type === "create" || modal?.type === "edit") && (
        <SampleModal
          trial={modal.type === "edit" ? modal.trial : null}
          opps={opps}
          onSave={() => { showToast("Sample saved"); setModal(null); load(); }}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}
